import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { 
    ArrowLeft,
    ChevronRight,
    Calendar as CalendarIcon,
    MapPin,
    Users,
    Check,
    X,
    HelpCircle
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const responseColors = {
    attending: 'bg-green-500',
    not_attending: 'bg-red-500',
    maybe: 'bg-amber-500',
};

export default function AppCalendarPage() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [showResponseDialog, setShowResponseDialog] = useState(false);

    useEffect(() => {
        fetchEvents();
    }, []);

    const fetchEvents = async () => {
        try {
            const response = await axios.get(`${API_URL}/activities`);
            // Filter out any events with invalid or missing start_time
            const validEvents = (response.data || []).filter(event => event && event.start_time);
            setEvents(validEvents);
        } catch (error) {
            console.error('Failed to fetch events:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleRespond = async (status) => {
        if (!selectedEvent) return;
        
        try {
            await axios.post(`${API_URL}/activities/${selectedEvent.id}/respond`, {
                activity_id: selectedEvent.id,
                status: status
            });
            toast.success(`Response updated: ${status.replace('_', ' ')}`);
            fetchEvents();
            setShowResponseDialog(false);
        } catch (error) {
            toast.error('Failed to update response');
        }
    };

    const formatDate = (isoString) => {
        if (!isoString) return { day: '', date: '', month: '', year: '' };
        const date = new Date(isoString);
        return {
            day: date.toLocaleDateString('en-AU', { weekday: 'short' }).toUpperCase(),
            date: date.getDate(),
            month: date.toLocaleDateString('en-AU', { month: 'long' }),
            year: date.getFullYear()
        };
    };

    const formatTime = (isoString) => {
        if (!isoString) return '';
        const date = new Date(isoString);
        return date.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' });
    };

    const getMyResponse = (event) => {
        if (!event || !event.responses || !user) return null;
        const myResponse = event.responses.find(r => r.user_id === user.id);
        return myResponse?.status;
    };

    // Group events by month
    const groupedEvents = events.reduce((acc, event) => {
        if (!event || !event.start_time) return acc;
        const date = new Date(event.start_time);
        if (isNaN(date.getTime())) return acc;
        const monthKey = `${date.toLocaleDateString('en-AU', { month: 'long' })}, ${date.getFullYear()}`;
        if (!acc[monthKey]) acc[monthKey] = [];
        acc[monthKey].push(event);
        return acc;
    }, {});

    // Sort months chronologically
    const sortedMonths = Object.keys(groupedEvents).sort((a, b) => {
        const dateA = new Date(a);
        const dateB = new Date(b);
        return dateA - dateB;
    });

    const getResponseCounts = (event) => {
        if (!event || !event.responses) return { attending: 0, total: 0 };
        const attending = event.responses.filter(r => r.status === 'attending').length;
        return { attending, total: event.responses.length };
    };

    const getActivityTypeColor = (type) => {
        const colors = {
            training: 'bg-blue-500',
            operations: 'bg-orange-500',
            meeting: 'bg-purple-500',
            event: 'bg-green-500',
            other: 'bg-gray-500'
        };
        return colors[type?.toLowerCase()] || colors.other;
    };

    return (
        <div className="min-h-screen bg-background" data-testid="app-calendar-page">
            {/* Header */}
            <header className="bg-primary text-white sticky top-0 z-30">
                <div className="flex items-center h-14 px-4">
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => navigate('/portal')}
                        className="text-white hover:bg-white/20 mr-2"
                        data-testid="calendar-back-btn"
                    >
                        <ArrowLeft className="w-5 h-5" />
                    </Button>
                    <h1 className="text-lg font-semibold">Calendar</h1>
                </div>
            </header>

            <div className="p-4">
                {loading ? (
                    <div className="p-8 text-center text-muted-foreground">
                        Loading events...
                    </div>
                ) : events.length === 0 ? (
                    <Card>
                        <CardContent className="p-8 text-center">
                            <CalendarIcon className="w-12 h-12 mx-auto mb-2 text-muted-foreground opacity-50" />
                            <p className="text-muted-foreground">No upcoming events</p>
                        </CardContent>
                    </Card>
                ) : (
                    <div className="space-y-6">
                        {sortedMonths.map((month) => (
                            <div key={month}>
                                <h2 className="text-lg font-semibold mb-3 text-muted-foreground">{month}</h2>
                                <Card>
                                    <CardContent className="p-0 divide-y">
                                        {groupedEvents[month].map((event) => {
                                            const dateInfo = formatDate(event.start_time);
                                            const myResponse = getMyResponse(event);
                                            const responseCounts = getResponseCounts(event);

                                            return (
                                                <button
                                                    key={event.id}
                                                    onClick={() => {
                                                        setSelectedEvent(event);
                                                        setShowResponseDialog(true);
                                                    }}
                                                    className="w-full p-4 text-left hover:bg-muted/30 transition-colors"
                                                    data-testid={`event-${event.id}`}
                                                >
                                                    <div className="flex items-start gap-3">
                                                        {/* Date column */}
                                                        <div className="flex flex-col items-center min-w-[45px]">
                                                            <span className="text-xs font-medium text-muted-foreground">
                                                                {dateInfo.day}
                                                            </span>
                                                            <span className="text-2xl font-bold">
                                                                {dateInfo.date}
                                                            </span>
                                                        </div>

                                                        {/* Event details */}
                                                        <div className="flex-1 min-w-0">
                                                            <div className="flex items-start gap-2">
                                                                <div className={`w-3 h-3 rounded-sm mt-1 ${getActivityTypeColor(event.activity_type)}`}></div>
                                                                <div className="flex-1">
                                                                    <p className="font-medium">{event.title}</p>
                                                                    <p className="text-sm text-muted-foreground">
                                                                        {formatTime(event.start_time)}
                                                                        {event.end_time && ` – ${formatTime(event.end_time)}`}
                                                                    </p>
                                                                    {event.location && (
                                                                        <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
                                                                            <MapPin className="w-3 h-3" />
                                                                            {event.location}
                                                                        </p>
                                                                    )}
                                                                </div>
                                                            </div>

                                                            {/* Response indicators */}
                                                            <div className="flex items-center gap-2 mt-2">
                                                                {/* Participant avatars */}
                                                                {event.responses && event.responses.length > 0 && (
                                                                    <div className="flex -space-x-2">
                                                                        {event.responses.slice(0, 4).map((resp, idx) => (
                                                                            <Avatar key={idx} className="w-6 h-6 border-2 border-background">
                                                                                <AvatarFallback className={`text-xs text-white ${responseColors[resp.status] || 'bg-gray-400'}`}>
                                                                                    {resp.user_name?.split(' ').map(n => n[0]).join('') || '?'}
                                                                                </AvatarFallback>
                                                                            </Avatar>
                                                                        ))}
                                                                        {event.responses.length > 4 && (
                                                                            <div className="w-6 h-6 rounded-full bg-muted flex items-center justify-center text-xs border-2 border-background">
                                                                                +{event.responses.length - 4}
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                )}
                                                                
                                                                {/* My response badge */}
                                                                {myResponse && (
                                                                    <Badge 
                                                                        variant="secondary" 
                                                                        className={`text-xs ${
                                                                            myResponse === 'attending' ? 'bg-green-100 text-green-700' :
                                                                            myResponse === 'not_attending' ? 'bg-red-100 text-red-700' :
                                                                            'bg-amber-100 text-amber-700'
                                                                        }`}
                                                                    >
                                                                        {myResponse === 'attending' ? 'Going' : 
                                                                         myResponse === 'not_attending' ? 'Not Going' : 'Maybe'}
                                                                    </Badge>
                                                                )}
                                                            </div>
                                                        </div>

                                                        <ChevronRight className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                                                    </div>
                                                </button>
                                            );
                                        })}
                                    </CardContent>
                                </Card>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Response Dialog */}
            <Dialog open={showResponseDialog} onOpenChange={setShowResponseDialog}>
                <DialogContent className="max-w-sm">
                    <DialogHeader>
                        <DialogTitle>{selectedEvent?.title}</DialogTitle>
                        <DialogDescription>
                            {selectedEvent?.start_time && (
                                <>
                                    {formatDate(selectedEvent.start_time).month} {formatDate(selectedEvent.start_time).date}, {formatDate(selectedEvent.start_time).year}
                                    <br />
                                    {formatTime(selectedEvent.start_time)}
                                    {selectedEvent?.end_time && ` – ${formatTime(selectedEvent.end_time)}`}
                                </>
                            )}
                        </DialogDescription>
                    </DialogHeader>

                    {selectedEvent?.description && (
                        <p className="text-sm text-muted-foreground">{selectedEvent.description}</p>
                    )}

                    {selectedEvent?.location && (
                        <p className="text-sm flex items-center gap-2">
                            <MapPin className="w-4 h-4" />
                            {selectedEvent.location}
                        </p>
                    )}

                    <div className="space-y-2 pt-4">
                        <p className="text-sm font-medium">Your Response</p>
                        <div className="grid grid-cols-3 gap-2">
                            <Button
                                variant={getMyResponse(selectedEvent) === 'attending' ? 'default' : 'outline'}
                                className={getMyResponse(selectedEvent) === 'attending' ? 'bg-green-600 hover:bg-green-700' : ''}
                                onClick={() => handleRespond('attending')}
                                data-testid="respond-attending"
                            >
                                <Check className="w-4 h-4 mr-1" />
                                Going
                            </Button>
                            <Button
                                variant={getMyResponse(selectedEvent) === 'maybe' ? 'default' : 'outline'}
                                className={getMyResponse(selectedEvent) === 'maybe' ? 'bg-amber-600 hover:bg-amber-700' : ''}
                                onClick={() => handleRespond('maybe')}
                                data-testid="respond-maybe"
                            >
                                <HelpCircle className="w-4 h-4 mr-1" />
                                Maybe
                            </Button>
                            <Button
                                variant={getMyResponse(selectedEvent) === 'not_attending' ? 'default' : 'outline'}
                                className={getMyResponse(selectedEvent) === 'not_attending' ? 'bg-red-600 hover:bg-red-700' : ''}
                                onClick={() => handleRespond('not_attending')}
                                data-testid="respond-not-attending"
                            >
                                <X className="w-4 h-4 mr-1" />
                                No
                            </Button>
                        </div>
                    </div>

                    {/* Responses list */}
                    {selectedEvent?.responses && selectedEvent.responses.length > 0 && (
                        <div className="pt-4 border-t">
                            <p className="text-sm font-medium mb-2 flex items-center gap-2">
                                <Users className="w-4 h-4" />
                                Responses ({selectedEvent.responses.length})
                            </p>
                            <div className="space-y-2 max-h-40 overflow-y-auto">
                                {selectedEvent.responses.map((resp, idx) => (
                                    <div key={idx} className="flex items-center justify-between text-sm">
                                        <span>{resp.user_name}</span>
                                        <Badge 
                                            variant="secondary"
                                            className={`text-xs ${
                                                resp.status === 'attending' ? 'bg-green-100 text-green-700' :
                                                resp.status === 'not_attending' ? 'bg-red-100 text-red-700' :
                                                'bg-amber-100 text-amber-700'
                                            }`}
                                        >
                                            {resp.status === 'attending' ? 'Going' : 
                                             resp.status === 'not_attending' ? 'Not Going' : 'Maybe'}
                                        </Badge>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
